import type { EventDetails } from "@/modules/eventDetails/client/api/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import { isAfter } from "date-fns"
import dynamic from "next/dynamic"
import { useParams } from "next/navigation"
import Skeleton from "react-loading-skeleton"

import { Divider as SparkDivider } from "@jobteaser/spark/components/Divider"
import { Heading } from "@jobteaser/spark/components/Heading"

import { Divider } from "@/modules/eventDetails/client/components/Divider/Divider"
import { Companies } from "@/modules/eventDetails/client/components/MainDetails/Companies"
import { EventStatus } from "@/modules/eventDetails/client/components/MainDetails/EventStatus"
import { Location } from "@/modules/eventDetails/client/components/MainDetails/Location"
import { MainButtonBlockUnlogged } from "@/modules/eventDetails/client/components/RegistrationDetails/MainButtonBlockUnlogged"
import { RegistrationDetails } from "@/modules/eventDetails/client/components/RegistrationDetails/RegistrationDetails"
import { useEventDetailsData } from "@/modules/eventDetails/client/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./MainDetails.module.css"

// The timezones are often different between server-side and client-side.
// Rendering only client-side avoids visual glitches and hydration errors.
const DynamicEventDates = dynamic(
  () => import("@/modules/eventDetails/client/components/MainDetails/EventDates").then(mod => mod.EventDates),
  {
    loading: () => (
      <Skeleton height="48px" containerClassName={clsx(styles.flexContainer, styles.eventDatesPlaceholder)} />
    ),
    ssr: false,
  }
)

export const MainDetails: FunctionComponent = () => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const { dataCurrentUser } = useCurrentUserFetch()
  const { companies } = event as EventDetails
  const hasCompanies = companies && companies.length > 0

  if (typeof event === "undefined") {
    return null
  }

  const { id, endDate } = event
  const currentDate = new Date()
  const isEventFinished = isAfter(currentDate, new Date(endDate))

  const isUnlogged = dataCurrentUser && !dataCurrentUser.isLogged
  const isWithoutRegistration = event.registrationDetails.type === "without_registration"

  return (
    <>
      <div className={styles.main} data-testid="event-details-main-details">
        <Heading
          as="h2"
          variant="title2"
          weight="bold"
          className={styles.title}
          data-testid="event-details-main-details-agenda"
        >
          {t("event_details.about.title")}
        </Heading>
        <DynamicEventDates />
        <Location />
        {hasCompanies && <Companies />}
        <SparkDivider className={styles.sparkDivider} />
        <div className={styles.registrationBlock}>
          {isUnlogged
            ? !isWithoutRegistration && <MainButtonBlockUnlogged />
            : !isEventFinished && <RegistrationDetails id={id} />}
          <EventStatus />
        </div>
      </div>
      <Divider className={styles.divider} />
    </>
  )
}
